import {buildSemanticTokens} from '../buildSemanticTokens';
import type {DesignSystemDefinition} from '../types';

/**
 * Monochrome proof theme — all brand/surface slots use gray scales so UI structure
 * can be validated without color. Swap ACTIVE_DESIGN_SYSTEM_VERSION to apply real palettes.
 */
const neutral = {
  'white-50': 'rgba(255, 255, 255, 0.5)',
  'primary-black': '#000000',
  900: '#111111',
  800: '#1a1a1a',
  700: '#2a2a2a',
  600: '#404040',
  500: '#525252',
  400: '#6b6b6b',
  300: '#8a8a8a',
  200: '#c4c4c4',
  100: '#e0e0e0',
  'black-50': '#f5f5f5',
  transparent: 'rgba(255, 255, 255, 0)',
};

/** Darker grays — maps to empionGreen (text, actions, accents) */
const greenColors = {
  2: '#0a0a0a',
  3: '#d0d0d0',
  4: '#c8c8c8',
  5: '#e8e8e8',
  30: '#7a7a7a',
  40: '#a3a3a3',
  50: '#ebebeb',
  70: '#8f8f8f',
  80: '#4a4a4a',
  100: '#5c5c5c',
  200: '#363636',
  500: '#404040',
  600: '#353535',
  700: '#2a2a2a',
  800: '#333333',
  850: '#6a6a6a',
  900: '#1a1a1a',
};

/** Lighter grays — maps to empionOrange (surfaces, chrome, page wash) */
const orangeColors = {
  3: '#c8c8c8',
  4: '#d4d4d4',
  5: '#ededed',
  6: '#fafafa',
  7: '#f5f5f5',
  40: '#a8a8a8',
  70: '#909090',
  100: '#787878',
  500: '#666666',
  800: '#d9d9d9',
  900: '#505050',
  950: '#404040',
};

const primary = {
  30: '#d9d9d9',
  50: '#a7a7a7',
  70: '#757575',
  100: '#565656',
  200: '#404040',
};

const monoScorePill = {
  high: {bgcolor: '#eeeeee', color: '#2d2d2d', strokeColor: '#707070'},
  medium: {bgcolor: '#e4e4e4', color: '#3d3d3d', strokeColor: '#909090'},
  low: {bgcolor: '#dcdcdc', color: '#4a4a4a', strokeColor: '#a8a8a8'},
};

const monoAlignmentStatus = {
  metAccent: '#505050',
  gapAccent: '#303030',
  gapLabel: '#404040',
  metChipBg: '#eeeeee',
  metChipFg: '#2d2d2d',
  gapChipBg: '#e8e8e8',
  gapChipFg: '#333333',
  warningChipBg: '#e4e4e4',
  warningChipFg: '#4a4a4a',
};

const semantic = buildSemanticTokens(
  {neutral, greenColors, orangeColors},
  {scorePill: monoScorePill, alignmentStatus: monoAlignmentStatus},
);

const pageGradient =
  'linear-gradient(180deg, #e0e0e0 0%, #f0f0f0 68.75%, #fafafa 100%)';

const chromeGradient =
  'linear-gradient(180deg, #0000 44%, white),' +
  ' radial-gradient(circle farthest-corner at 100% 0%, rgba(80, 80, 80, 0.12) 7%, #fff0 73%),' +
  ' radial-gradient(circle farthest-corner at 0% 0%, #d8d8d8, #f5f5f5)';

export const designSystemMonochrome: DesignSystemDefinition = {
  version: 'monochrome',
  neutral,
  greenColors,
  orangeColors,
  primary,
  warningColor: '#4a4a4a',
  warningColorLight: '#c8c8c8',
  warningColorDark: '#303030',
  actions: {
    cta: greenColors[500],
    ctaHover: greenColors[700],
    ctaDisabled: orangeColors[40],
    primary: greenColors[700],
    primaryHover: greenColors[100],
    destructive: '#4a4a4a',
    destructiveHover: '#303030',
  },
  accent: {
    main: greenColors[500],
    subtle: greenColors[5],
    icon: greenColors[30],
  },
  text: {
    primary: greenColors[700],
    secondary: neutral[600],
  },
  empionOrangeGradient: {
    base: pageGradient,
    medium: chromeGradient,
    dark:
      'radial-gradient(circle farthest-corner at 0% 50%, #d8d8d8, #fff0 62%),' +
      ' radial-gradient(circle farthest-side at 100% 0%, #b8b8b8, #fff0 38%)',
    navbar: chromeGradient,
  },
  scrollbar: {
    track: greenColors[5],
    thumb: greenColors[70],
    thumbHover: orangeColors[40],
  },
  lightModePalette: {
    primary: {
      main: greenColors[500],
      contrastText: '#ffffff',
    },
    secondary: {
      main: orangeColors[900],
      light: orangeColors[500],
      dark: orangeColors[950],
    },
    background: {
      default: orangeColors[5],
      paper: '#ffffff',
      menu: '#ffffff',
      dark: orangeColors[3],
    },
    text: {
      primary: greenColors[700],
      secondary: neutral[600],
    },
    info: {
      main: greenColors[500],
      contrastText: '#ffffff',
    },
    warning: {
      main: orangeColors[900],
      contrastText: '#ffffff',
    },
  },
  ...semantic,
  profileComparisonSelection: {
    slotA: {
      bgcolor: '#ececec',
      color: '#404040',
      borderColor: '#707070',
    },
    slotB: {
      bgcolor: '#e0e0e0',
      color: '#333333',
      borderColor: '#909090',
    },
  },
};
